import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  RocketLaunchIcon,
  SignalIcon,
  ClockIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  PlayIcon,
  PlusIcon,
  XMarkIcon,
  InformationCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import {
  AppBadge,
  Badge,
  Btn,
  Card,
  StatusDot,
  Sparkline,
  cx,
  makeSpark,
} from '../../../components/admin/console';
import {
  useApps,
  useChannels,
  usePointers,
  useOtaDevices,
  useOtaAudit,
} from './data/hooks';
import { channelBadgeTone, channelLabel } from './data/channelLabels';
import type {
  AuditEntry,
  ChannelPointer,
  OtaApp,
  OtaAppId,
  OtaChannelId,
} from './data/types';
import { isBetaReleaseChannel } from './data/channelLabels';
import { formatDistanceToNow } from 'date-fns';

export function OtaOverviewPage() {
  const navigate = useNavigate();
  const { data: apps = [] } = useApps();
  const { data: channels = [] } = useChannels();
  const { data: pointers = [] } = usePointers();
  const { data: devices = [] } = useOtaDevices();
  const { data: audit = [] } = useOtaAudit();

  const devicesReporting = devices.filter((d) => d.offlineDays < 2).length;
  const stale = devices.filter((d) => d.vnVc <= 208).length;
  const autoRollbacks = devices.filter((d) => d.autoRollback).length;
  const activeRollouts = pointers.filter(
    (p) => p.status === 'rolling' || (p.maxDevices != null && p.served < p.maxDevices),
  ).length;

  // Derive risks from real conditions (no scenario tweak in production builds).
  const risks: RiskBannerProps[] = [];
  const haltedPointer = pointers.find((p) => p.status === 'halted');
  const rollbackVictim = devices.find((d) => d.autoRollback);
  if (rollbackVictim) {
    risks.push({
      level: 'error',
      title: `Auto-rollback triggered — VortexNav ${rollbackVictim.flaggedBuild} general release`,
      body: `${rollbackVictim.vessel} (${rollbackVictim.id}) auto-rolled back from ${rollbackVictim.flaggedBuild} → ${rollbackVictim.vnVc} after ${rollbackVictim.crashes} crashes_last_24h. haltOnAnyRollback freezes the rollout.`,
      actions: [
        { label: 'Inspect on Coverage', onClick: () => navigate('/admin/releases/coverage') },
      ],
    });
  }
  if (haltedPointer) {
    risks.push({
      level: 'warning',
      title: `Release channel halted — ${haltedPointer.app}`,
      body: `Pointer is frozen at vc ${haltedPointer.current}. Investigate the cause then resume from the Channels page.`,
      actions: [{ label: 'Open release channels', onClick: () => navigate('/admin/releases/channels') }],
    });
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">
            OTA Update Console
          </div>
          <h1 className="text-2xl font-bold text-white mt-1">Overview</h1>
          <p className="text-sm text-slate-400 mt-1">
            Channel × app health across the fleet. Drill into any cell to manage a channel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="outline" size="md" leftIcon={<ArrowDownTrayIcon className="w-3.5 h-3.5" />}>
            Export audit CSV
          </Btn>
          <Btn
            variant="accent"
            size="md"
            leftIcon={<RocketLaunchIcon className="w-3.5 h-3.5" />}
            onClick={() => navigate('/admin/releases/builds')}
          >
            Promote a build
          </Btn>
        </div>
      </div>

      {risks.map((r, i) => (
        <RiskBanner key={i} {...r} />
      ))}

      <div className="grid grid-cols-4 gap-4">
        <Kpi
          icon={<SignalIcon className="w-[18px] h-[18px]" />}
          color="emerald"
          title="Devices reporting (24h)"
          value={devicesReporting}
          total={devices.length}
          trend="+4 vs yesterday"
        />
        <Kpi
          icon={<ClockIcon className="w-[18px] h-[18px]" />}
          color="amber"
          title="On stale builds (>30d)"
          value={stale}
          total={devices.length}
          trend="3 underway now"
        />
        <Kpi
          icon={<ArrowUturnLeftIcon className="w-[18px] h-[18px]" />}
          color={autoRollbacks ? 'red' : 'slate'}
          title="Auto-rollbacks (7d)"
          value={autoRollbacks}
          trend={autoRollbacks ? 'vn 211 general' : 'clean'}
        />
        <Kpi
          icon={<RocketLaunchIcon className="w-[18px] h-[18px]" />}
          color="cyan"
          title="Active rollouts"
          value={activeRollouts}
          trend="vn · general 4/8"
        />
      </div>

      <div className="grid grid-cols-[1fr_320px] gap-6 items-start">
        <Card padded={false} className="overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/60">
            <div>
              <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                Channel × app matrix
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Hover a cell for details · click to open that channel
              </p>
            </div>
            <div className="flex items-center gap-3 text-[11px] text-slate-500">
              <LegendDot color="emerald" /> healthy
              <LegendDot color="amber" pulse /> rolling
              <LegendDot color="red" /> halted
              <LegendDot color="slate" /> no pointer
            </div>
          </div>
          <Matrix
            apps={apps}
            channels={channels}
            pointers={pointers}
            onOpenCell={() => navigate('/admin/releases/channels')}
          />
        </Card>

        <Card padded={false} className="overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-700/60 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                Live activity
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Last 20 audit events</p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/admin/releases/audit')}
              className="text-[11px] text-cyan-400 hover:text-cyan-300 font-semibold"
            >
              View all →
            </button>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {audit.slice(0, 20).map((a, i) => (
              <ActivityRow key={i} evt={a} />
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

interface RiskBannerProps {
  level: 'error' | 'warning';
  title: string;
  body: string;
  actions: Array<{ label: string; onClick: () => void; danger?: boolean }>;
}

function RiskBanner({ level, title, body, actions }: RiskBannerProps) {
  const map =
    level === 'error'
      ? { border: 'border-red-500/40',   bg: 'bg-red-500/10',   icon: 'text-red-400',   dot: 'bg-red-400' }
      : { border: 'border-amber-500/40', bg: 'bg-amber-500/10', icon: 'text-amber-400', dot: 'bg-amber-400' };
  return (
    <div className={cx('rounded-xl border p-4 flex items-start gap-4', map.border, map.bg)}>
      <div
        className={cx(
          'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-900/50',
          map.icon,
        )}
      >
        <ExclamationTriangleIcon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cx('w-1.5 h-1.5 rounded-full animate-pulse', map.dot)} />
          <div className="text-[15px] font-bold text-white">{title}</div>
        </div>
        <p className="text-sm text-slate-300 mt-1 leading-relaxed">{body}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {actions.map((a) => (
          <Btn key={a.label} variant={a.danger ? 'danger' : 'outline'} size="sm" onClick={a.onClick}>
            {a.label}
          </Btn>
        ))}
      </div>
    </div>
  );
}

interface KpiProps {
  icon: ReactNode;
  color: 'emerald' | 'amber' | 'red' | 'cyan' | 'slate';
  title: string;
  value: number;
  total?: number;
  trend: string;
}

const kpiBg: Record<KpiProps['color'], string> = {
  emerald: 'bg-emerald-500/10 text-emerald-400',
  amber:   'bg-amber-500/10 text-amber-400',
  red:     'bg-red-500/10 text-red-400',
  cyan:    'bg-cyan-500/10 text-cyan-400',
  slate:   'bg-slate-600/20 text-slate-400',
};

function Kpi({ icon, color, title, value, total, trend }: KpiProps) {
  return (
    <Card>
      <div className="flex items-center gap-3 mb-3">
        <div className={cx('w-9 h-9 rounded-lg flex items-center justify-center', kpiBg[color])}>
          {icon}
        </div>
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">{title}</p>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="text-3xl font-bold text-white font-mono leading-none">{value}</div>
        {total != null && <div className="text-sm text-slate-500 font-mono">/ {total}</div>}
      </div>
      <div className="mt-2 text-[11px] text-slate-500">{trend}</div>
    </Card>
  );
}

function LegendDot({ color, pulse = false }: { color: 'emerald' | 'amber' | 'red' | 'slate'; pulse?: boolean }) {
  const c =
    color === 'emerald' ? 'bg-emerald-400'
    : color === 'amber' ? 'bg-amber-400'
    : color === 'red'   ? 'bg-red-400'
    : 'bg-slate-500';
  return (
    <span className="flex items-center gap-1">
      <span className={cx('w-1.5 h-1.5 rounded-full', c, pulse && 'pulse-amber')} />
    </span>
  );
}

interface MatrixProps {
  apps: readonly OtaApp[];
  channels: readonly { id: OtaChannelId; label: string }[];
  pointers: ChannelPointer[];
  onOpenCell: (channel: OtaChannelId, app: OtaAppId) => void;
}

function Matrix({ apps, channels, pointers, onOpenCell }: MatrixProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="w-32 text-left px-5 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Channel
            </th>
            {apps.map((a) => (
              <th key={a.id} className="px-2 py-2 text-left">
                <div className="flex items-center gap-2">
                  <AppBadge app={a.id} size="sm" />
                  <div>
                    <div className="text-[12px] font-semibold text-white leading-tight">{a.name}</div>
                    <div className="text-[9px] font-mono text-slate-500 uppercase tracking-wider leading-tight">
                      {a.id}
                    </div>
                  </div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {channels.map((ch) => (
            <tr
              key={ch.id}
              className={cx(
                'border-t border-slate-700/50',
                isBetaReleaseChannel(ch.id) && 'bg-slate-900/20',
              )}
            >
              <td className="px-5 py-3 align-top">
                <div className="text-sm font-semibold text-white">{ch.label}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">
                  {ch.id} stream
                </div>
              </td>
              {apps.map((a) => {
                const p = pointers.find((x) => x.channel === ch.id && x.app === a.id);
                return (
                  <td key={a.id} className="p-1.5">
                    <MatrixCell pointer={p} appId={a.id} onClick={() => onOpenCell(ch.id, a.id)} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MatrixCell({
  pointer,
  appId,
  onClick,
}: {
  pointer: ChannelPointer | undefined;
  appId: OtaAppId;
  onClick: () => void;
}) {
  if (!pointer) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full min-h-[82px] px-3 py-2 rounded-lg border border-dashed border-slate-700 text-slate-600 text-xs hover:border-slate-600 hover:text-slate-400 transition-colors text-left"
      >
        no pointer
      </button>
    );
  }
  const sparkSeed = pointer.current * 7 + appId.charCodeAt(0);
  const s = makeSpark(sparkSeed, pointer.served || 3);
  const statusText =
    pointer.status === 'rolling' ? `rolling ${pointer.served}/${pointer.maxDevices}`
    : pointer.status === 'healthy' ? `${pointer.served} device${pointer.served === 1 ? '' : 's'}`
    : pointer.status === 'halted' ? 'halted'
    : '—';
  const color =
    pointer.status === 'rolling' ? 'text-amber-400'
    : pointer.status === 'halted' ? 'text-red-400'
    : 'text-emerald-400';
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full min-h-[82px] rounded-lg border bg-slate-900/40 hover:bg-slate-900/80 transition-all cell-pop text-left p-2.5 border-slate-700/70 hover:border-cyan-500/40"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[10px] text-slate-500 font-mono">vc</span>
          <span className="text-base font-mono font-bold text-white leading-none">
            {pointer.current}
          </span>
        </div>
        <StatusDot status={pointer.status} />
      </div>
      <div className="mt-1.5 flex items-center justify-between">
        <span className={cx('text-[10px] font-semibold', color)}>{statusText}</span>
        <span className={color}>
          <Sparkline data={s} width={40} height={14} />
        </span>
      </div>
    </button>
  );
}

const actionIcon: Record<AuditEntry['action'], ReactNode> = {
  promote:         <RocketLaunchIcon className="w-3.5 h-3.5" />,
  halt:            <HandRaisedIcon className="w-3.5 h-3.5" />,
  resume:          <PlayIcon className="w-3.5 h-3.5" />,
  'widen-rollout': <PlusIcon className="w-3.5 h-3.5" />,
  rollback:        <ArrowUturnLeftIcon className="w-3.5 h-3.5" />,
  'prune-build':   <XMarkIcon className="w-3.5 h-3.5" />,
};
const actionTone: Record<AuditEntry['action'], string> = {
  promote: 'text-cyan-400',
  halt: 'text-red-400',
  resume: 'text-emerald-400',
  'widen-rollout': 'text-amber-400',
  rollback: 'text-red-400',
  'prune-build': 'text-slate-500',
};

function ActivityRow({ evt }: { evt: AuditEntry }) {
  return (
    <div className="px-5 py-3 border-b border-slate-700/50 last:border-b-0 hover:bg-slate-700/20 transition-colors">
      <div className="flex items-center gap-2.5">
        <span className={actionTone[evt.action]}>
          {actionIcon[evt.action] ?? <InformationCircleIcon className="w-3.5 h-3.5" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-semibold text-white capitalize">
              {evt.action.replace('-', ' ')}
            </span>
            <AppBadge app={evt.app} size="sm" />
            {evt.channel && (
              <Badge tone={channelBadgeTone(evt.channel)}>{channelLabel(evt.channel)}</Badge>
            )}
          </div>
          {evt.from != null && (
            <div className="text-[11px] text-slate-400 font-mono mt-0.5 flex items-center gap-1">
              {evt.from} <ArrowRightIcon className="w-2.5 h-2.5 inline" /> {evt.to ?? '—'}
            </div>
          )}
        </div>
        <div className="text-[10px] text-slate-500 font-mono">
          {formatDistanceToNow(new Date(evt.t), { addSuffix: true })}
        </div>
      </div>
      <div className="mt-1 pl-[22px] text-[11px] text-slate-500 truncate">
        {evt.actor} · {evt.reason}
      </div>
    </div>
  );
}

export default OtaOverviewPage;
